import { DocumentOrientation, PossibleFlyerType, TemplateConfig } from "pages/flyer/builder/drawer";
import { defaultQrCode } from "pages/flyer/builder/drawer/new-tldraw-components/consts";
import { useEffect, useRef } from "react";
import { AssetRecordType, Editor } from "@tldraw/tldraw";
import { isRuntimeFormat } from "pages/flyer/builder/formats/formatsStore";
import { getZoneSizesPx } from "pages/flyer/builder/formats/bleed";
import { syncFoldLines } from "pages/flyer/builder/formats/foldLines";
import { getStoredOrientation } from "pages/flyer/builder/utils/orientationStorage";
import { centerCameraOnBothCanvas } from "pages/flyer/builder/drawer/new-tldraw-components/simpleHtmlExport";
import * as shapes from "./snapshots";
import { useStore } from "store";
import { StoreType } from "store/types";

const shapeCreators = {
  DOOR_HANGER: shapes.shapesDOOR_HANGER,
  TEARAWAY: shapes.shapesTEARAWAY,
};

const SHAPE_VARIANT_CREATORS = {
  door_hanger: shapes.shapesDOOR_HANGER,
  tearaway: shapes.shapesTEARAWAY,
};

export const useFlyerLayout = (
  editor: Editor,
  flyerType: string,
  currentFormat: string,
  currentFormatConfig: TemplateConfig,
  isUpdateShapesPosition?: boolean,
  designId?: string,
  campaignId?: string,
  currentOrientation?: string
) => {
  // Read orientation: prefer prop, then localStorage, then format default
  // For ADM/EDDM, if prop is empty, default to Landscape (not the format's defaultOrientation)
  // to avoid a flash of Portrait before the state propagates
  const isADMOrEDDM = flyerType?.startsWith("POSTCARD_") || flyerType?.startsWith("EDDM_") || flyerType === "EDDM";
  const fallbackOrientation = isADMOrEDDM ? DocumentOrientation.Landscape : currentFormatConfig.defaultOrientation;
  const storedOrientation = currentOrientation || getStoredOrientation(designId, campaignId, fallbackOrientation);

  const isPortrait = storedOrientation === DocumentOrientation.Portrait;
  const catalogueFormat = isRuntimeFormat(currentFormatConfig) ? currentFormatConfig : null;
  const isEnableLandScape = catalogueFormat
    ? catalogueFormat.allowLandscape
    : flyerType !== "TEARAWAY" && flyerType !== "DOOR_HANGER";

  // Determine display dimensions based on orientation, accounting for natural format orientation.
  // Some formats (ADM postcards) have displayWidth > displayHeight (naturally landscape).
  // When the user wants portrait, we need to swap to ensure width < height.
  const cfgW = currentFormatConfig.displayWidth;
  const cfgH = currentFormatConfig.displayHeight;
  const isNaturallyLandscape = cfgW > cfgH;

  let displayWidth: number;
  let displayHeight: number;

  if (isPortrait) {
    displayWidth = isNaturallyLandscape ? cfgH : cfgW;
    displayHeight = isNaturallyLandscape ? cfgW : cfgH;
  } else if (!isEnableLandScape) {
    displayWidth = cfgW;
    displayHeight = cfgH;
  } else {
    // Landscape
    displayWidth = isNaturallyLandscape ? cfgW : cfgH;
    displayHeight = isNaturallyLandscape ? cfgH : cfgW;
  }

  const snapshot = editor.getSnapshot();
  const existSnapshot = !!(snapshot.document.store && (snapshot.document.store as any)["shape:front"]);

  // Track previous values to detect actual changes (not initial load)
  const prevValuesRef = useRef<{
    flyerType: string;
    currentFormat: string;
    storedOrientation: string;
  } | null>(null);

  // Track previous isUpdateShapesPosition to detect when it becomes true
  const prevIsUpdateShapesPositionRef = useRef(false);

  useEffect(() => {
    // Skip layout if orientation hasn't been determined yet — prevents initial flash
    // of wrong spacing when switching to ADM/EDDM formats
    if (!storedOrientation) return;

    const centerX = 0;
    const centerY = 0;

    const frontBgAssetId = AssetRecordType.createId("front");
    const logo = AssetRecordType.createId("logo");
    const qrCode = AssetRecordType.createId("qrCode");

    const { safetyZonePx, bleedsZonePx } = getZoneSizesPx(currentFormatConfig);

    const shapeCreator =
      (catalogueFormat &&
        SHAPE_VARIANT_CREATORS[catalogueFormat.shapeVariant as keyof typeof SHAPE_VARIANT_CREATORS]) ||
      shapeCreators[flyerType as keyof typeof shapeCreators] ||
      shapes.snapshotBaseFormat;

    // Detect orientation change: previous orientation differs from current
    // Skip if this change came from undo sync — tldraw already reverted the shapes.
    const isUndoSync = (useStore.getState() as StoreType).isUndoOrientationSync;
    const isOrientationSwitch =
      !isUndoSync && prevValuesRef.current !== null && prevValuesRef.current.storedOrientation !== storedOrientation;

    // Reset the undo sync flag AFTER reading it (both BottomToolPanel and here need it)
    if (isUndoSync) {
      (useStore.getState() as StoreType).setIsUndoOrientationSync(false);
    }

    shapeCreator?.(
      editor,
      isPortrait,
      centerX,
      centerY,
      displayWidth,
      displayHeight,
      frontBgAssetId,
      safetyZonePx,
      bleedsZonePx,
      logo,
      qrCode,
      existSnapshot,
      isUpdateShapesPosition,
      currentFormatConfig.defaultOrientation,
      isOrientationSwitch
    );

    // Fold guides follow the catalogue's panel widths; legacy formats have none and clear any stale guides
    syncFoldLines(editor, {
      panelWidthsMm: catalogueFormat?.panelWidthsMm ?? [],
      widthMm: currentFormatConfig.widthInMm,
      heightMm: currentFormatConfig.heightInMm,
    });

    // Only center camera if format or orientation actually changed (not on initial load)
    const isInitialLoad = prevValuesRef.current === null;
    const formatChanged = prevValuesRef.current?.currentFormat !== currentFormat;
    const orientationChanged = prevValuesRef.current?.storedOrientation !== storedOrientation;
    const flyerTypeChanged = prevValuesRef.current?.flyerType !== flyerType;

    // Check if isUpdateShapesPosition just became true (positions are being reset)
    const isPositionResetTriggered = !prevIsUpdateShapesPositionRef.current && isUpdateShapesPosition;

    // Update refs with current values
    prevValuesRef.current = {
      flyerType,
      currentFormat,
      storedOrientation,
    };
    prevIsUpdateShapesPositionRef.current = isUpdateShapesPosition ?? false;

    // Center camera if:
    // 1. Format/orientation/flyerType changed (not initial load), OR
    // 2. isUpdateShapesPosition just became true (positions being reset)
    if ((!isInitialLoad && (formatChanged || orientationChanged || flyerTypeChanged)) || isPositionResetTriggered) {
      // Use longer delay when positions are being reset
      const delay = isPositionResetTriggered ? 500 : 200;

      const timeoutId = setTimeout(() => {
        if (editor && typeof editor.getCurrentPageShapes === "function") {
          const shapes = editor.getCurrentPageShapes();
          const frontShape = shapes.find((shape: any) => shape.id === "shape:frontBlackLine");
          const backShape = shapes.find((shape: any) => shape.id === "shape:backBlackLine");

          if (frontShape && backShape) {
            centerCameraOnBothCanvas(editor);
          } else {
            // If shapes are not ready, retry after additional delay
            setTimeout(() => {
              if (editor && typeof editor.getCurrentPageShapes === "function") {
                const retryShapes = editor.getCurrentPageShapes();
                const retryFrontShape = retryShapes.find((shape: any) => shape.id === "shape:frontBlackLine");
                const retryBackShape = retryShapes.find((shape: any) => shape.id === "shape:backBlackLine");

                if (retryFrontShape && retryBackShape) {
                  centerCameraOnBothCanvas(editor);
                }
              }
            }, 300);
          }
        }
      }, delay);

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [
    flyerType,
    currentFormat,
    displayWidth,
    displayHeight,
    isPortrait,
    isUpdateShapesPosition,
    storedOrientation,
    editor,
    catalogueFormat,
  ]);
};
